// --------------------------------------------------------------
// Created On: 2021-10-27
// Author: Zachary Thomas
//
// Last Modified: 2026-10-15
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import styles from "./NavigationDrawerLink.module.scss";

// A single selectable page link.
export default function NavigationDrawerLink(props: Props): Component {
  const [assetId, setAssetId] = useState<string>("0");
  const location = useLocation();
  const targetPage = getTargetPage(props.targetPage, assetId, props.linksToAsset);

  // Update the asset ID based on the current location.
  useEffect(() => {
    const pathSubdirectories = location.pathname.split("/");
    if (pathSubdirectories.length > 2) {
      setAssetId(pathSubdirectories[2]);
    }
  }, [location.pathname]);

  // Fill in the asset ID for links that point to an asset page.
  function getTargetPage(targetPage: string, assetId: string, linksToAsset?: boolean): string {
    if (linksToAsset) {
      return targetPage.replace("*", assetId);
    } else {
      return targetPage;
    }
  }

  // Treat partial matches as complete matches, unless exact is enabled.
  function isActive(targetPage: string, currentPage: string, exactMatch?: boolean): boolean {
    if (exactMatch) {
      return targetPage === currentPage;
    } else {
      const currentSubdirectories = currentPage.split("/");
      return targetPage.split("/").every((subdirectory, i) => currentSubdirectories[i] === subdirectory);
    }
  }

  return (
    <Link className={`${styles.body} ${styles.navigationLink}`} to={targetPage} onClick={() => props.onClick()}>
      <div
        data-test="nav-drawer-link"
        className={`${styles.link} p-3 ${
          isActive(targetPage, location.pathname, props.exactMatch) ? styles.selected : ""
        }`}
      >
        {props.name}
      </div>
    </Link>
  );
}

interface Props {
  name: string;
  targetPage: string;
  linksToAsset?: boolean;
  exactMatch?: boolean;
  onClick: () => void;
}
